export interface LruCache<K, V> {
  readonly get: (key: K) => V | undefined;
  readonly has: (key: K) => boolean;
  readonly set: (key: K, value: V) => void;
  readonly delete: (key: K) => void;
  readonly clear: () => void;
  readonly size: () => number;
}

export function createLruCache<K, V>(maxEntries: number): LruCache<K, V> {
  const entries = new Map<K, V>();
  const limit = Math.max(1, Math.floor(maxEntries));

  function touch(key: K, value: V): void {
    entries.delete(key);
    entries.set(key, value);
  }

  return {
    get(key) {
      const value = entries.get(key);
      if (value === undefined && !entries.has(key)) {
        return;
      }
      touch(key, value as V);
      return value;
    },
    has(key) {
      return entries.has(key);
    },
    set(key, value) {
      touch(key, value);
      while (entries.size > limit) {
        const oldest = entries.keys().next().value;
        if (oldest === undefined) {
          return;
        }
        entries.delete(oldest);
      }
    },
    delete(key) {
      entries.delete(key);
    },
    clear() {
      entries.clear();
    },
    size() {
      return entries.size;
    },
  };
}
